const HEX = /^#?([0-9a-f]{6})$/i;
const BRAND_VARS = ['--primary', '--primary-foreground', '--ring', '--accent', '--accent-foreground'] as const;

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function hexToHsl(hex: string): Hsl | null {
  const match = HEX.exec(hex.trim());
  if (!match) return null;
  const value = parseInt(match[1], 16);
  const r = ((value >> 16) & 255) / 255;
  const g = ((value >> 8) & 255) / 255;
  const b = (value & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: Math.round(h * 60), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function triplet({ h, s, l }: Hsl): string {
  return `${h} ${s}% ${l}%`;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

export function brandTokens(hex: string, dark: boolean): Record<(typeof BRAND_VARS)[number], string> | null {
  const base = hexToHsl(hex);
  if (!base) return null;
  const primary = dark ? { ...base, s: clamp(base.s - 10, 30, 90), l: clamp(Math.max(base.l, 45), 45, 62) } : { ...base, l: clamp(base.l, 22, 48) };
  const foreground = primary.l > 58 ? '222 47% 8%' : '0 0% 100%';
  const accent = dark ? { h: base.h, s: clamp(base.s - 20, 20, 60), l: 20 } : { h: base.h, s: clamp(base.s - 20, 25, 70), l: 95 };
  const accentForeground = dark ? '210 40% 96%' : triplet({ ...primary, l: clamp(primary.l - 4, 18, 40) });
  return {
    '--primary': triplet(primary),
    '--primary-foreground': foreground,
    '--ring': triplet(primary),
    '--accent': triplet(accent),
    '--accent-foreground': accentForeground,
  };
}

let current: string | null = null;

function paint() {
  const root = document.documentElement;
  if (!current) {
    for (const name of BRAND_VARS) root.style.removeProperty(name);
    return;
  }
  const tokens = brandTokens(current, root.classList.contains('dark'));
  if (!tokens) return;
  for (const [name, value] of Object.entries(tokens)) root.style.setProperty(name, value);
}

export function applyBrandColor(hex: string | null | undefined): void {
  if (typeof document === 'undefined') return;
  const next = hex && HEX.test(hex.trim()) ? hex.trim() : null;
  if (next === current) return;
  current = next;
  paint();
}

export function repaintBrandForTheme(): void {
  if (typeof document === 'undefined' || !current) return;
  paint();
}
